import React from 'react';
import { FiX } from 'react-icons/fi';

interface ExamModeDialogProps {
  isOpen: boolean;
  examDays: number;
  onDaysChange: (value: number) => void;
  examOptions: string[];
  gradeOptions: string[];
  selectedExamType: string;
  onExamTypeChange: (value: string) => void;
  showCustomExamInput: boolean;
  customExamType: string;
  onCustomExamTypeChange: (value: string) => void;
  customExamError: string | null;
  expectedGrade: string;
  onExpectedGradeChange: (value: string) => void;
  scoreType: string;
  onScoreTypeChange: (value: string) => void;
  scoreValue: string;
  onScoreValueChange: (value: string) => void;
  scoreError: string | null;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  onClose: () => void;
}

const ExamModeDialog: React.FC<ExamModeDialogProps> = ({
  isOpen,
  examDays,
  onDaysChange,
  examOptions,
  gradeOptions,
  selectedExamType,
  onExamTypeChange,
  showCustomExamInput,
  customExamType,
  onCustomExamTypeChange,
  customExamError,
  expectedGrade,
  onExpectedGradeChange,
  scoreType,
  onScoreTypeChange,
  scoreValue,
  onScoreValueChange,
  scoreError,
  onSubmit,
  isSubmitting,
  submitError,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="exam-mode-overlay" onClick={onClose}>
      <div className="exam-mode-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="exam-mode-header">
          <h2>Exam Mode</h2>
          <button className="exam-mode-close" onClick={onClose} aria-label="Close exam mode">
            <FiX size={24} />
          </button>
        </div>
        <div className="exam-mode-content">
          <div className="exam-mode-section">
            <h3>Number of Days</h3>
            <div className="exam-days">
              <input
                type="number"
                min="30"
                max="90"
                value={examDays}
                onChange={(e) => onDaysChange(parseInt(e.target.value, 10))}
                className="exam-days-input"
                placeholder="Enter number of days"
              />
              <span className="exam-days-hint">(30-365 days)</span>
            </div>
          </div>

          <div className="exam-mode-section">
            <h3>Exam Type</h3>
            <div className="exam-type">
              <select value={selectedExamType} onChange={(e) => onExamTypeChange(e.target.value)} className="exam-type-select">
                <option value="">Select Exam Type</option>
                {examOptions.map((exam) => (
                  <option key={exam} value={exam}>
                    {exam}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            {showCustomExamInput && (
              <div className="custom-exam-input">
                <input
                  type="text"
                  value={customExamType}
                  onChange={(e) => onCustomExamTypeChange(e.target.value)}
                  placeholder="Enter custom exam type"
                  className="custom-exam-text-input"
                />
                {customExamError && (
                  <div className="custom-exam-error" style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                    {customExamError}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="exam-mode-section">
            <h3>Expected Grade</h3>
            <div className="exam-grade">
              <select value={expectedGrade} onChange={(e) => onExpectedGradeChange(e.target.value)} className="exam-grade-select">
                <option value="">Select Expected Grade</option>
                {gradeOptions.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="exam-mode-section">
            <h3>Target Score</h3>
            <div className="score-selection">
              <div className="score-type-selector">
                <label>
                  <input
                    type="radio"
                    name="scoreType"
                    value="percentage"
                    checked={scoreType === 'percentage'}
                    onChange={(e) => onScoreTypeChange(e.target.value)}
                  />
                  <span>Percentage</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="scoreType"
                    value="score"
                    checked={scoreType === 'score'}
                    onChange={(e) => onScoreTypeChange(e.target.value)}
                  />
                  <span>Score (1-5)</span>
                </label>
              </div>
              <div className="score-input-container">
                {scoreType === 'percentage' && (
                  <div className="score-input-group">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scoreValue}
                      onChange={(e) => onScoreValueChange(e.target.value)}
                      placeholder="Enter percentage (0-100)"
                      className="score-input"
                    />
                    <span className="score-unit">%</span>
                  </div>
                )}
                {scoreType === 'score' && (
                  <div className="score-input-group">
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={scoreValue}
                      onChange={(e) => onScoreValueChange(e.target.value)}
                      placeholder="Enter score (1-5)"
                      className="score-input"
                    />
                    <span className="score-unit">/5</span>
                  </div>
                )}
                {scoreError && (
                  <div className="score-error" style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                    {scoreError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="exam-mode-actions">
            <button className="exam-start-btn" onClick={onSubmit} disabled={isSubmitting || !!scoreError}>
              {isSubmitting ? 'Generating…' : 'Start Exam'}
            </button>
            <button className="exam-cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
          {submitError && (
            <div className="sessions-error" style={{ marginTop: 8 }}>
              <span className="error-text">{submitError}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamModeDialog;
